import ClientComponent from '@/components/ClientComponent/ClientComponent';
import { ChannelVideo } from '@/typings/channelVideo';
import { VideoCategory } from '@/typings/videoCategory';
import { firebaseAdmin } from '@/utils/firebaseAdmin';
import ChannelVideoDetail from '@/views/ChannelVideoDetail/ChannelVideoDetail';
import { GetStaticPaths, GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

type VideoDetailPageProps = {
  video: ChannelVideo;
  relatedVideos: ChannelVideo[];
  channelVideos: ChannelVideo[];
  categories: VideoCategory[];
};

const VideoDetailPage: React.FC<VideoDetailPageProps> = (props) => {
  return (
    <>
      <NextSeo title={props.video.title} />
      <ClientComponent>
        <ChannelVideoDetail {...props} />
      </ClientComponent>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const db = firebaseAdmin.firestore();
  const channelVideosCollection = db.collection('channelVideos');
  const { docs } = await channelVideosCollection.where('deletedAt', '==', null).get();
  const channelVideos = docs.map((doc) => ({ ...doc.data(), id: doc.id })) as ChannelVideo[];

  const paths = channelVideos
    .filter((video) => !!video.slug)
    .map((video: ChannelVideo) => {
      return {
        params: { slug: video.slug },
      };
    });
  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const slug = context.params?.slug;

  if (!slug) {
    return {
      notFound: true,
    };
  }

  const db = firebaseAdmin.firestore();
  const channelVideosCollection = db.collection('channelVideos');
  const { docs } = await channelVideosCollection
    .where('slug', '==', slug)
    .where('deletedAt', '==', null)
    .get();

  const video: ChannelVideo | null = docs[0]
    ? {
        ...(docs[0].data() as ChannelVideo),
        id: docs[0].id,
      }
    : null;

  if (!video)
    return {
      notFound: true,
    };

  const { docs: allVideosDocs } = await channelVideosCollection
    .where('deletedAt', '==', null)
    .orderBy('order', 'asc')
    .get();

  const allVideos: ChannelVideo[] = (allVideosDocs || []).map((doc) => {
    return {
      ...(doc.data() as ChannelVideo),
      id: doc.id,
    };
  });

  if (!allVideos) {
    return {
      notFound: true,
    };
  }

  let category: VideoCategory | null = null;
  let relatedVideoDocs: firebaseAdmin.firestore.QueryDocumentSnapshot<firebaseAdmin.firestore.DocumentData>[] =
    [];

  if (video.categoryId) {
    const doc = await db.collection('videoCategories').doc(video.categoryId).get();
    category = { ...(doc.data() as VideoCategory), id: doc.id };

    const { docs: videoDocs } = await channelVideosCollection
      .where('categoryId', '==', video.categoryId)
      .where('deletedAt', '==', null)
      .orderBy('order', 'asc')
      .get();
    relatedVideoDocs = videoDocs;
  } else {
    const { docs: videoDocs } = await channelVideosCollection
      .orderBy('slug', 'asc')
      .where('slug', '!=', slug)
      .where('deletedAt', '==', null)
      .orderBy('order', 'asc')
      .get();
    relatedVideoDocs = videoDocs;
  }

  const relatedVideos = relatedVideoDocs.map((doc) => ({
    ...(doc.data() as ChannelVideo),
    id: doc.id,
    category,
  }));

  const allOtherVideos = allVideos.filter((video) => {
    if (video.categoryId !== null) {
      return video.slug !== slug && video.categoryId !== category?.id;
    }
    return false;
  });

  const filteredRelatedVideos = relatedVideos.filter((video) => video.slug !== slug);
  const combinedVideos = [...filteredRelatedVideos, ...allOtherVideos];
  return {
    props: {
      video: JSON.parse(JSON.stringify({ ...video, category })),
      relatedVideos: JSON.parse(
        JSON.stringify(combinedVideos.length > 2 ? combinedVideos.slice(0, 8) : combinedVideos),
      ),
      locale: context.locale,
    },
    revalidate: 2592000,
  };
};

export default VideoDetailPage;
